<template>
  <div class="order-list">
      <!-- 页面标题和统计 -->
      <div class="page-header">
        <div class="header-content">
          <div class="title-section">
            <h2 class="page-title">
              <i class="el-icon-document"></i> 订单列表
            </h2>
            <p class="page-subtitle">管理和查看所有订单信息</p>
          </div>
          <div class="stats-section" v-if="statistics.totalOrders > 0">
            <div class="stat-item">
              <div class="stat-number">{{ statistics.totalOrders }}</div>
              <div class="stat-label">总订单数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">¥{{ statistics.totalAmount }}</div>
              <div class="stat-label">总金额</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ statistics.pendingPayment }}</div>
              <div class="stat-label">待支付</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 搜索表单 -->
      <div class="search-form">
        <el-collapse v-model="searchCollapse" class="search-collapse">
          <el-collapse-item title="高级搜索" name="1">
            <el-form :model="searchForm" ref="searchForm" :inline="true" class="search-form-content">
              <el-row :gutter="20">
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item label="订单号">
                    <el-input
                      v-model="searchForm.OrderNo"
                      placeholder="请输入订单号"
                      clearable>
                    </el-input>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item label="预订员ID">
                    <el-input
                      v-model="searchForm.BookerID"
                      placeholder="请输入预订员ID（测试用户: 9CG01L68）"
                      clearable>
                    </el-input>
                    <div style="font-size: 12px; color: #909399; margin-top: 4px;" v-if="searchForm.BookerID">
                      当前: {{ searchForm.BookerID }}
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item label="订单状态">
                    <el-select v-model="searchForm.OrderStatus" placeholder="请选择状态" clearable>
                      <el-option label="全部" value=""></el-option>
                      <el-option label="待支付" value="待支付"></el-option>
                      <el-option label="已支付" value="已支付"></el-option>
                      <el-option label="已出票" value="已出票"></el-option>
                      <el-option label="已取消" value="已取消"></el-option>
                      <el-option label="已完成" value="已完成"></el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item label="订单日期">
                    <el-date-picker
                      v-model="dateRange"
                      type="daterange"
                      range-separator="至"
                      start-placeholder="开始日期"
                      end-placeholder="结束日期"
                      value-format="yyyy-MM-dd"
                      style="width: 100%;">
                    </el-date-picker>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item label="每页条数">
                    <el-select v-model="searchForm.PageSize">
                      <el-option label="10条" :value="10"></el-option>
                      <el-option label="20条" :value="20"></el-option>
                      <el-option label="50条" :value="50"></el-option>
                      <el-option label="100条" :value="100"></el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :sm="12" :md="8" :lg="6">
                  <el-form-item>
                    <el-button type="primary" @click="handleSearch" :loading="loading">
                      <i class="el-icon-search"></i> 查询
                    </el-button>
                    <el-button @click="handleReset">
                      <i class="el-icon-refresh"></i> 重置
                    </el-button>
                    <el-button type="success" @click="handleExport" :loading="exporting">
                      <i class="el-icon-download"></i> 导出
                    </el-button>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </el-collapse-item>
        </el-collapse>
      </div>

      <!-- 快捷筛选 -->
      <div class="quick-filters" v-if="quickFilters.length > 0">
        <el-button
          v-for="filter in quickFilters"
          :key="filter.value"
          :type="activeQuickFilter === filter.value ? 'primary' : 'default'"
          size="small"
          @click="applyQuickFilter(filter)">
          {{ filter.label }} ({{ filter.count }})
        </el-button>
      </div>

      <!-- 订单列表 -->
      <div class="order-table">
        <div class="table-header">
          <div class="table-title">
            <span>订单列表</span>
            <el-tag v-if="orderList.length > 0" type="info" size="small">
              共 {{ total }} 条记录
            </el-tag>
          </div>
          <div class="table-actions">
            <el-button-group>
              <el-button size="mini" @click="refreshList" :loading="loading">
                <i class="el-icon-refresh"></i> 刷新
              </el-button>
            </el-button-group>
          </div>
        </div>

        <el-table
          :data="orderList"
          v-loading="loading"
          element-loading-text="正在加载订单数据..."
          element-loading-background="rgba(255, 255, 255, 0.8)"
          style="width: 100%"
          border
          stripe
          :default-sort="{prop: 'createTime', order: 'descending'}"
          @sort-change="handleSortChange"
          @selection-change="handleSelectionChange"
          empty-text="暂无订单数据，请调整搜索条件或稍后再试">

          <el-table-column type="selection" width="55"></el-table-column>

          <el-table-column prop="orderNo" label="订单号" width="140" show-overflow-tooltip sortable="custom">
            <template slot-scope="scope">
              <div class="order-cell">
                <el-link type="primary" @click="viewOrderDetail(scope.row)" :underline="false">
                  <i class="el-icon-document-copy"></i> {{ scope.row.orderNo || '未知订单号' }}
                </el-link>
                <div class="order-tags" v-if="scope.row.IsUrgent">
                  <el-tag size="mini" type="danger">紧急</el-tag>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="bookerName" label="预订员" width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.bookerName || '未知客户' }}
            </template>
          </el-table-column>

          <el-table-column prop="orderTotalAmount" label="订单金额" width="120" align="right" sortable="custom">
            <template slot-scope="scope">
              <div class="amount-cell">
                <span class="price">¥{{ formatAmount(scope.row.orderTotalAmount) }}</span>
                <div class="original-price" v-if="scope.row.OriginalAmount && scope.row.OriginalAmount !== scope.row.orderTotalAmount">
                  <del>¥{{ formatAmount(scope.row.OriginalAmount) }}</del>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="orderStatusText" label="订单状态" width="110">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.orderStatus)" effect="dark">
                {{ scope.row.orderStatusText || getStatusText(scope.row.orderStatus) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="createTime" label="创建时间" width="160" sortable="custom">
            <template slot-scope="scope">
              <div class="time-cell">
                <div>{{ formatDate(scope.row.createTime, 'yyyy-MM-dd') }}</div>
                <div class="time">{{ formatDate(scope.row.createTime, 'hh:mm:ss') }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="passengerCount" label="旅客数量" width="100" align="center">
            <template slot-scope="scope">
              <el-tag type="info" effect="plain">
                {{ scope.row.passengerCount || 1 }}人
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="flightInfo" label="航班信息" min-width="280" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="flight-cell" v-if="scope.row.flightInfo">
                <!-- 优先显示行程信息 -->
                <div v-if="scope.row.flightInfo.intro" class="flight-intro">
                  <i class="el-icon-route"></i>
                  {{ scope.row.flightInfo.intro }}
                </div>
                <!-- 如果没有行程信息，显示航班详情 -->
                <div v-else-if="scope.row.flightInfo.departureCity || scope.row.flightInfo.arrivalCity" class="flight-route">
                  <i class="el-icon-location-outline"></i>
                  {{ scope.row.flightInfo.departureCity || '未知' }} → {{ scope.row.flightInfo.arrivalCity || '未知' }}
                </div>
                <div class="flight-details" v-if="!scope.row.flightInfo.intro && (scope.row.flightInfo.flightNo || scope.row.flightInfo.departureDate)">
                  <el-tag size="mini" type="info">{{ scope.row.flightInfo.flightNo || '未知航班' }}</el-tag>
                  <span class="flight-date" v-if="scope.row.flightInfo.departureDate">{{ formatDate(scope.row.flightInfo.departureDate, 'MM-dd') }}</span>
                </div>
              </div>
              <span v-else class="no-data">-</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="160" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-tooltip content="查看详情" placement="top">
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-view"
                    circle
                    @click="viewOrderDetail(scope.row)">
                  </el-button>
                </el-tooltip>
                <el-tooltip content="支付" placement="top" v-if="scope.row.IsPayable">
                  <el-button
                    size="mini"
                    type="success"
                    icon="el-icon-coin"
                    circle
                    @click="handlePay(scope.row)">
                  </el-button>
                </el-tooltip>
                <el-tooltip content="取消订单" placement="top" v-if="scope.row.CanCancel">
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-close"
                    circle
                    @click="handleCancel(scope.row)">
                  </el-button>
                </el-tooltip>
                <el-tooltip content="更多操作" placement="top">
                  <el-dropdown @command="handleMoreAction" trigger="click">
                    <el-button size="mini" type="info" icon="el-icon-more" circle></el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item :command="{action: 'print', order: scope.row}">
                        <i class="el-icon-printer"></i> 打印订单
                      </el-dropdown-item>
                      <el-dropdown-item :command="{action: 'email', order: scope.row}">
                        <i class="el-icon-message"></i> 发送邮件
                      </el-dropdown-item>
                      <el-dropdown-item :command="{action: 'copy', order: scope.row}">
                        <i class="el-icon-document-copy"></i> 复制订单号
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper" v-if="total > 0">
          <div class="pagination-info">
            <span>显示第 {{ (searchForm.PageCount - 1) * searchForm.PageSize + 1 }} 至 {{ Math.min(searchForm.PageCount * searchForm.PageSize, total) }} 条，共 {{ total }} 条记录</span>
          </div>
          <el-pagination
            background
            :current-page="searchForm.PageCount"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="searchForm.PageSize"
            layout="prev, pager, next, sizes, jumper, total"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="pageChange">
          </el-pagination>
        </div>
      </div>

      <!-- 批量操作栏 -->
      <div class="batch-actions" v-if="selectedOrders.length > 0">
        <div class="batch-info">
          已选择 <el-tag type="primary">{{ selectedOrders.length }}</el-tag> 个订单
        </div>
        <div class="batch-buttons">
          <el-button size="small" @click="batchExport">批量导出</el-button>
          <el-button size="small" type="danger" @click="batchCancel">批量取消</el-button>
        </div>
      </div>
  </div>
</template>

<script>
import { getSalesOrderList, generateOrderListParams } from '@/api/orderListApi'
import { getSessionKey } from '@/utils/localdatas'
import { getBookerID, isUsingTestUser } from '@/utils/util'
import logger from '@/utils/logger'

export default {
  name: 'OrderList',
  data() {
    return {
      loading: false,
      exporting: false,
      orderList: [],
      total: 0,
      dateRange: [],
      searchCollapse: ['1'],
      selectedOrders: [],
      activeQuickFilter: '',
      sortBy: 'createTime',
      sortOrder: 'descending',
      searchForm: {
        OrderNo: '',
        BookerID: '',
        OrderStatus: '',
        PageCount: 1,
        PageSize: 10,
        StartDate: '',
        EndDate: '',
        yqfkey: '',
        time: '',
        clientplatform: 'H5',
        mpid: '11',
        useraccountno: '',
        userCompanyCode: '',
        userCompanyName: '',
        userRoleID: 0,
        userOrganizaType: 'INCU',
        userBizAccount: ''
      },
      statistics: {
        totalOrders: 0,
        totalAmount: 0,
        pendingPayment: 0
      },
      quickFilters: [
        { label: '全部', value: '', count: 0 },
        { label: '待支付', value: '待支付', count: 0 },
        { label: '已支付', value: '已支付', count: 0 },
        { label: '已出票', value: '已出票', count: 0 },
        { label: '已取消', value: '已取消', count: 0 }
      ],
    }
  },
  created() {
    logger.log('🚀 订单列表组件已创建')
    logger.log('📋 当前路由:', this.$route.path)
    this.initSearchParams()
    // 延迟一点加载，确保组件完全初始化
    this.$nextTick(() => {
      logger.log('🔄 开始加载订单列表数据')
      this.loadOrderList()
    })
  },
  mounted() {
    logger.log('✅ 订单列表组件已挂载')
    logger.log('📊 当前订单列表数量:', this.orderList.length)
    logger.log('⏳ 加载状态:', this.loading)
    
    // 如果3秒后数据仍然为空且不在加载中，显示模拟数据
    setTimeout(() => {
      if (this.orderList.length === 0 && !this.loading) {
        logger.log('⚠️ 组件挂载3秒后数据仍为空，显示模拟数据')
        this.showMockData()
      }
    }, 3000)
  },
  methods: {
    // 初始化搜索参数
    initSearchParams() {
      // 使用统一的BookerID获取函数（优先登录用户，否则使用测试用户）
      const bookerID = getBookerID()

      // 设置默认日期范围为最近30天
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)

      this.dateRange = [
        this.formatDate(startDate, 'yyyy-MM-dd'),
        this.formatDate(endDate, 'yyyy-MM-dd')
      ]

      this.searchForm = {
        OrderNo: '',
        BookerID: bookerID, // 使用统一的BookerID获取函数
        OrderStatus: '',
        PageCount: 1,
        PageSize: 10,
        StartDate: this.dateRange[0],
        EndDate: this.dateRange[1],
        yqfkey: sessionKey || '',
        time: new Date().getTime(),
        clientplatform: 'H5',
        mpid: '11',
        useraccountno: accountInfo?.useraccountno || accountInfo?.Account || '',
        userCompanyCode: accountInfo?.userCompanyCode || accountInfo?.CompanyCode || '',
        userCompanyName: accountInfo?.userCompanyName || accountInfo?.CompanyName || '',
        userRoleID: accountInfo?.userRoleID || accountInfo?.RoleID || 0,
        userOrganizaType: accountInfo?.userOrganizaType || accountInfo?.OrganizaType || 'INCU',
        userBizAccount: accountInfo?.userBizAccount || accountInfo?.BizAccount || ''
      }
    },

    // 加载订单列表
    async loadOrderList() {
      this.loading = true

      try {
        // 更新日期范围
        if (this.dateRange && this.dateRange.length === 2) {
          this.searchForm.StartDate = this.dateRange[0]
          this.searchForm.EndDate = this.dateRange[1]
        }

        // 更新时间戳
        this.searchForm.time = new Date().getTime()

        // 添加排序参数
        const params = {
          ...generateOrderListParams(this.searchForm),
          SortBy: this.sortBy,
          SortOrder: this.sortOrder
        }

        logger.log('📤 订单列表请求参数:', params)

        // 设置超时，如果5秒内没有响应，显示模拟数据
        const timeoutId = setTimeout(() => {
          if (this.loading) {
            logger.warn('⏱️ API请求超时，显示模拟数据')
            this.loading = false
            this.showMockData()
          }
        }, 5000)

        getSalesOrderList(params, (result) => {
          clearTimeout(timeoutId)
          this.loading = false
          logger.log('📊 订单列表API响应:', result)

          // 解析API响应数据 - 根据真实API返回结构
          try {
            let orderData = []
            let totalCount = 0

            // 检查API返回的数据结构 - 根据实际API响应格式调整
            if (result && result.code === 1 && result.data) {
              // 正确的API响应结构
              let apiData = result.data

              // 处理不同的数据格式
              if (Array.isArray(apiData)) {
                // 直接返回数组
                orderData = apiData
                totalCount = apiData.length
              } else if (apiData.Items && Array.isArray(apiData.Items)) {
                // 标准分页格式
                orderData = apiData.Items
                totalCount = parseInt(apiData.TotalCount) || apiData.Items.length
              } else if (apiData.SalesOrders && Array.isArray(apiData.SalesOrders)) {
                // 真实API返回的SalesOrders嵌套结构
                logger.log('🎯 找到SalesOrders数组，长度:', apiData.SalesOrders.length)
                logger.log('📋 SalesOrders[0]样本数据:', apiData.SalesOrders[0])
                // 验证SOShortNr字段
                if (apiData.SalesOrders.length > 0) {
                  const sampleOrder = apiData.SalesOrders[0]
                  logger.log('🔍 订单号字段验证:', {
                    SOShortNr: sampleOrder.SOShortNr,
                    OrderNo: sampleOrder.OrderNo,
                    YQFOrderNo: sampleOrder.YQFOrderNo
                  })
                }
                orderData = apiData.SalesOrders
                totalCount = parseInt(apiData.TotalCount) || apiData.SalesOrders.length
              } else if (apiData.List && Array.isArray(apiData.List)) {
                // 通用列表格式
                orderData = apiData.List
                totalCount = parseInt(apiData.TotalCount) || apiData.List.length
              } else if (apiData.OrderList && Array.isArray(apiData.OrderList)) {
                // 订单列表格式
                orderData = apiData.OrderList
                totalCount = parseInt(apiData.TotalCount) || apiData.OrderList.length
              } else {
                console.warn('⚠️ 无法识别的数据格式:', apiData)
                // 尝试直接解析为订单数组
                if (typeof apiData === 'object' && Object.keys(apiData).length > 0) {
                  // 可能是单个订单对象，转换为数组
                  orderData = [apiData]
                  totalCount = 1
                }
              }

              if (orderData.length > 0) {
                // 处理真实订单数据 - 根据实际API字段映射
                this.orderList = this.processOrderData(orderData)
                this.total = totalCount

                // 更新统计信息
                const stats = apiData.Statistics || result.statistics || {}
                this.updateStatistics(stats)

                // 更新快捷筛选计数
                const statusCounts = apiData.StatusCounts || result.statusCounts || {}
                this.updateQuickFilters(statusCounts)

                console.log('✅ 成功解析真实订单数据:', this.orderList.length, '条记录')
                console.log('💰 订单总金额:', this.orderList.reduce((sum, order) => sum + (parseFloat(order.orderTotalAmount) || 0), 0))
              } else {
                console.log('📋 当前查询条件无订单数据')
                this.orderList = []
                this.total = 0

                // 即使没有数据也更新统计信息
                this.updateStatistics({})
                this.updateQuickFilters({})
              }
            } else {
              // API调用失败或返回格式不正确
              const errorMsg = result?.msg || result?.message || '获取订单列表失败'
              logger.warn('⚠️ API响应不成功:', result)
              logger.warn('⚠️ 响应代码:', result?.code)
              logger.warn('⚠️ 响应数据:', result?.data)
              
              // 如果code是-2，说明是网络错误，直接显示模拟数据
              if (result?.code === -2 || result?.code === undefined) {
                logger.log('🔧 网络错误或API未响应，显示模拟数据')
                this.showMockData()
              } else {
                // 其他错误，显示提示并显示模拟数据
                this.$message.warning(errorMsg)
                this.showMockData()
              }
            }

            // 处理分页边界情况
            if (this.orderList.length === 0 && this.searchForm.PageCount > 1) {
              logger.log('📄 当前页无数据，返回第一页')
              this.searchForm.PageCount = 1
              this.loadOrderList()
            }
          } catch (error) {
            logger.error('❌ 解析API响应时出错:', error)
            logger.error('❌ 错误详情:', error.stack)
            this.$message.error('解析订单数据时出错，显示模拟数据')
            this.showMockData()
          }
        })
      } catch (error) {
        this.loading = false
        logger.error('❌ 加载订单列表失败:', error)
        logger.error('❌ 错误详情:', error.stack)
        this.$message.warning('网络请求失败，显示模拟数据')
        this.showMockData()
      }
    },

    // 显示模拟数据
    showMockData() {
      console.log('🔧 显示模拟订单数据')
      try {
        const mockOrders = this.generateMockOrderList()
        console.log('🔧 生成的模拟数据数量:', mockOrders.length)
        console.log('🔧 生成的模拟数据样本:', mockOrders.slice(0, 2)) // 显示前2条
        
        const processedOrders = this.processOrderData(mockOrders)
        console.log('🔧 处理后的订单数据数量:', processedOrders.length)
        console.log('🔧 处理后的订单数据样本:', processedOrders.slice(0, 2)) // 显示前2条
        
        this.orderList = processedOrders
        this.total = mockOrders.length

        // 更新统计信息
        const totalAmount = mockOrders.reduce((sum, order) => sum + parseFloat(order.OrderTotalAmount || 0), 0)
        this.updateStatistics({
          TotalOrders: mockOrders.length,
          TotalAmount: totalAmount,
          PendingPayment: mockOrders.filter(order => order.OrderStatus === '待支付').length
        })

        // 更新快捷筛选计数
        const statusCounts = {}
        mockOrders.forEach(order => {
          statusCounts[order.OrderStatus] = (statusCounts[order.OrderStatus] || 0) + 1
        })
        this.updateQuickFilters(statusCounts)
        
        console.log('✅ 模拟数据已加载，订单数量:', this.orderList.length)
        console.log('✅ 总记录数:', this.total)
      } catch (error) {
        console.error('❌ 生成模拟数据时出错:', error)
        // 即使出错，也设置一个空数组，避免页面报错
        this.orderList = []
        this.total = 0
        this.updateStatistics({})
        this.updateQuickFilters({})
      }
    },

    // 生成模拟订单数据
    // 生成真实API格式的订单号 (如 BA2RSA, CA3BJD)
    generateRealisticOrderCode(index) {
      const airlineCodes = ['BA', 'CA', 'MU', 'CZ', 'HU', 'FM', '3U', 'ZH', 'SC', 'JD', '9C', 'HO']
      const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
      const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

      const airline = airlineCodes[Math.floor(Math.random() * airlineCodes.length)]
      const number = numbers[Math.floor(Math.random() * numbers.length)]
      const letter1 = letters[Math.floor(Math.random() * letters.length)]
      const letter2 = letters[Math.floor(Math.random() * letters.length)]
      const letter3 = letters[Math.floor(Math.random() * letters.length)]

      return `${airline}${number}${letter1}${letter2}${letter3}`
    },

    generateMockOrderList() {
      const orders = []
      const statuses = ['待支付', '已支付', '已出票', '已取消', '已完成']
      const cities = ['北京', '上海', '广州', '深圳', '成都', '杭州', '西安', '重庆', '南京', '武汉']
      const airlines = ['CA', 'MU', 'CZ', 'HU', 'FM', '3U', 'ZH', 'SC']

      for (let i = 1; i <= 20; i++) {
        const departure = cities[Math.floor(Math.random() * cities.length)]
        let arrival = cities[Math.floor(Math.random() * cities.length)]
        while (arrival === departure) {
          arrival = cities[Math.floor(Math.random() * cities.length)]
        }

        const createdTime = new Date()
        createdTime.setDate(createdTime.getDate() - Math.floor(Math.random() * 30))

        // 生成真实API格式的订单号
        const orderCode = this.generateRealisticOrderCode(i)

        orders.push({
          // 使用新的字段结构，匹配processOrderData的映射逻辑
          SOShortNr: orderCode, // 使用真实API格式的订单号
          YQFOrderNo: orderCode,
          OrderNo: orderCode,
          BookerName: `客户${1000 + i}`,
          OrderTotalAmount: (Math.random() * 10000 + 500).toFixed(2),
          TotalAmount: (Math.random() * 10000 + 500).toFixed(2),
          OrderStatus: statuses[Math.floor(Math.random() * statuses.length)],
          LatestDocStatus: statuses[Math.floor(Math.random() * statuses.length)],
          OrderDate: createdTime.toISOString(),
          CreateTime: createdTime.toISOString(),
          PassengerCount: Math.floor(Math.random() * 5) + 1,
          Intro: `${departure} → ${arrival} 航班之旅`, // 订单行程信息
          FlightInfo: {
            DepartureCity: departure,
            ArrivalCity: arrival,
            FlightNumber: `${airlines[Math.floor(Math.random() * airlines.length)]}${Math.floor(Math.random() * 9000 + 1000)}`,
            DepartureDate: new Date(createdTime.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000)
          }
        })
      }

      return orders.sort((a, b) => new Date(b.CreateTime) - new Date(a.CreateTime))
    },

    // 处理订单数据，添加计算字段
    processOrderData(orders) {
      logger.log('🔄 开始处理订单数据，数量:', orders.length)
      return orders.map((order, index) => {
        // 调试：打印前几个订单的SOShortNr字段
        if (index < 3) {
          logger.log(`📝 订单${index + 1}的SOShortNr:`, order.SOShortNr)
        }
        // 提取行程信息 - 优先从模拟数据的Intro字段获取，然后从ProductSalesOrders数组合并
        let tripInfo = order.Intro || '' // 首先尝试直接获取Intro字段（模拟数据）
        if (!tripInfo && order.ProductSalesOrders && Array.isArray(order.ProductSalesOrders)) {
          // 如果没有直接Intro字段，从ProductSalesOrders数组合并
          const introList = order.ProductSalesOrders
            .map(subOrder => subOrder.Intro || '')
            .filter(intro => intro.trim() !== '')
          tripInfo = introList.join('；')
        }

        // 根据真实API字段进行映射和标准化
        const processedOrder = {
          // 保持原始数据引用，便于调试
          rawData: order,

          // 基本订单信息 - 映射API字段到前端字段
          id: order.ID || order.SalesOrderID || Math.random().toString(),
          orderNo: order.SOShortNr || '', // 用户指定SOShortNr是订单号
          pnrCode: order.PNRCode || order.PNR || '',

          // 客户信息
          bookerName: order.BookerName || '未知客户',
          bookerID: order.BookerID || '',
          passengerCount: order.PassengerCount || order.ProductSalesOrders?.length || 1,

          // 金额信息 - 确保转换为数字
          orderTotalAmount: parseFloat(order.TotalAmount || order.OrderTotalAmount || 0),
          ticketAmount: parseFloat(order.TicketAmount || 0),
          otherAmount: parseFloat(order.OtherAmount || 0),

          // 订单状态 - 映射状态值
          orderStatus: order.LatestDocStatus || 'New', // 使用真实API字段
          orderStatusText: this.getOrderStatusText(order.LatestDocStatusCName || order.LatestDocStatus),
          paymentStatus: order.PaymentStatus || 'UnPay',
          paymentStatusText: this.getPaymentStatusText(order.PaymentStatus),
          ticketStatus: order.TicketStatus || 'NotTicketed',
          ticketStatusText: this.getTicketStatusText(order.TicketStatus),

          // 订单类型
          orderType: order.OrderType || 'Flight',
          orderTypeText: this.getOrderTypeText(order.OrderType),

          // 时间信息 - 确保转换为Date对象
          createTime: order.OrderDate ? new Date(order.OrderDate) : (order.CreateTime ? new Date(order.CreateTime) : new Date()), // 优先使用OrderDate，然后CreateTime
          updatedTime: order.UpdatedTime ? new Date(order.UpdatedTime) : new Date(),
          paymentTime: order.PaymentTime ? new Date(order.PaymentTime) : null,
          ticketTime: order.TicketTime ? new Date(order.TicketTime) : null,

          // 航班信息（如果是机票订单）
          flightInfo: {
            flightRoute: tripInfo || '', // 使用合并的行程信息
            intro: tripInfo || '', // 用户指定Intro是订单行程
            departureDate: order.DepartureDate || '',
            departureCity: order.DepartureCity || '',
            arrivalCity: order.ArrivalCity || '',
            airline: order.Airline || '',
            flightNo: order.FlightNo || '',
            cabinClass: order.CabinClass || ''
          },

          // 乘机人信息 - 从ProductSalesOrders中提取
          passengers: order.ProductSalesOrders || [],

          // 其他信息
          remark: order.Remark || '',
          source: order.Source || '',
          salesID: order.SalesID || '',
          isInternational: order.IsInternational || false,

          // 计算字段
          IsUrgent: this.isUrgentOrder(order.OrderDate), // 使用真实API字段
          IsPayable: this.checkIsPayable(order.LatestDocStatus, order.PaymentStatus),
          CanCancel: this.checkCanCancel(order.LatestDocStatus, order.PaymentStatus),
          CanRefund: this.checkCanRefund(order.LatestDocStatus, order.TicketStatus),
          CanChange: this.checkCanChange(order.LatestDocStatus, order.TicketStatus),

          // 显示相关 - FlightInfo用于模板显示，保持为对象格式
          FlightInfo: tripInfo ? { intro: tripInfo } : (this.extractFlightInfo(order) || this.generateMockFlightInfo())
        }

        return processedOrder
      })
    },

    // 检查是否可支付
    checkIsPayable(orderStatus, paymentStatus) {
      const payableStatuses = ['待支付', 'UnPay', 'New']
      return payableStatuses.includes(orderStatus) || payableStatuses.includes(paymentStatus)
    },

    // 检查是否可取消
    checkCanCancel(orderStatus, paymentStatus) {
      const cancelableStatuses = ['待支付', '已支付', 'UnPay', 'Paid', 'New']
      return cancelableStatuses.includes(orderStatus) || cancelableStatuses.includes(paymentStatus)
    },

    // 检查是否可退票
    checkCanRefund(orderStatus, ticketStatus) {
      const refundableStatuses = ['已出票', 'Ticketed', 'Completed']
      return refundableStatuses.includes(orderStatus) || refundableStatuses.includes(ticketStatus)
    },

    // 检查是否可改签
    checkCanChange(orderStatus, ticketStatus) {
      const changeableStatuses = ['已出票', 'Ticketed']
      return changeableStatuses.includes(orderStatus) || changeableStatuses.includes(ticketStatus)
    },

    // 从订单数据中提取航班信息
    extractFlightInfo(order) {
      if (order.FlightRoute || order.FlightNo) {
        return {
          DepartureCity: order.DepartureCity || '',
          ArrivalCity: order.ArrivalCity || '',
          FlightNumber: order.FlightNo || order.FlightNumber || '',
          FlightRoute: order.FlightRoute || '',
          DepartureDate: order.DepartureDate || '',
          Airline: order.Airline || '',
          CabinClass: order.CabinClass || ''
        }
      }
      return null
    },

    // 判断是否为紧急订单
    isUrgentOrder(createdTime) {
      if (!createdTime) return false
      const orderTime = new Date(createdTime).getTime()
      const now = new Date().getTime()
      const hoursDiff = (now - orderTime) / (1000 * 60 * 60)
      return hoursDiff < 24
    },

    // 生成模拟航班信息（用于演示）
    generateMockFlightInfo() {
      const cities = ['北京', '上海', '广州', '深圳', '成都', '杭州', '西安', '重庆']
      const airlines = ['CA', 'MU', 'CZ', 'HU', 'FM', '3U']

      const departure = cities[Math.floor(Math.random() * cities.length)]
      let arrival = cities[Math.floor(Math.random() * cities.length)]
      while (arrival === departure) {
        arrival = cities[Math.floor(Math.random() * cities.length)]
      }

      return {
        DepartureCity: departure,
        ArrivalCity: arrival,
        FlightNumber: `${airlines[Math.floor(Math.random() * airlines.length)]}${Math.floor(Math.random() * 9000 + 1000)}`,
        DepartureDate: new Date()
      }
    },

    // 更新统计信息
    updateStatistics(stats) {
      this.statistics = {
        totalOrders: stats.TotalOrders || this.total,
        totalAmount: this.formatAmount(stats.TotalAmount || 0),
        pendingPayment: stats.PendingPayment || 0
      }
    },

    // 更新快捷筛选
    updateQuickFilters(counts) {
      this.quickFilters = this.quickFilters.map(filter => ({
        ...filter,
        count: counts[filter.value] || 0
      }))
    },

    // 搜索
    handleSearch() {
      this.searchForm.PageCount = 1
      this.activeQuickFilter = ''
      this.loadOrderList()
    },

    // 重置
    handleReset() {
      this.$refs.searchForm.resetFields()
      this.dateRange = []
      this.activeQuickFilter = ''
      this.initSearchParams()
      this.loadOrderList()
    },

    // 刷新列表
    refreshList() {
      this.loadOrderList()
    },

    // 分页变化
    pageChange(page) {
      this.searchForm.PageCount = page
      this.loadOrderList()
    },

    // 每页条数变化
    handleSizeChange(size) {
      this.searchForm.PageSize = size
      this.searchForm.PageCount = 1
      this.loadOrderList()
    },

    // 排序变化
    handleSortChange({ prop, order }) {
      this.sortBy = prop
      this.sortOrder = order
      this.loadOrderList()
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedOrders = selection
    },

    // 应用快捷筛选
    applyQuickFilter(filter) {
      this.activeQuickFilter = filter.value
      this.searchForm.OrderStatus = filter.value
      this.searchForm.PageCount = 1
      this.loadOrderList()
    },

    // 查看订单详情
    viewOrderDetail(order) {
      const orderId = order.orderNo || order.id
      if (!orderId) {
        this.$message.warning('订单号不存在')
        return
      }
      // 在新窗口打开订单详情
      const routeData = this.$router.resolve({
        name: 'MemberOrderDetail',
        params: { soShortNr: orderId }
      })
      window.open(routeData.href, '_blank')
    },

    // 处理支付
    handlePay(order) {
      const orderId = order.orderNo || order.id
      const amount = order.orderTotalAmount || 0
      this.$confirm(`确认支付订单 ${orderId}，金额 ¥${this.formatAmount(amount)}？`, '确认支付', {
        confirmButtonText: '确认支付',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$message.success('支付功能开发中...')
        // this.$router.push({
        //   name: 'Payment',
        //   params: { orderId: orderId }
        // })
      }).catch(() => {})
    },

    // 处理取消订单
    handleCancel(order) {
      const orderId = order.orderNo || order.id
      this.$confirm(`确认取消订单 ${orderId}？取消后无法恢复。`, '确认取消', {
        confirmButtonText: '确认取消',
        cancelButtonText: '暂不取消',
        type: 'warning'
      }).then(() => {
        // 这里应该调用取消订单的API
        this.$message.success('订单已取消')
        this.loadOrderList()
      }).catch(() => {})
    },

    // 处理更多操作
    handleMoreAction({ action, order }) {
      switch (action) {
        case 'print':
          this.printOrder(order)
          break
        case 'email':
          this.sendEmail(order)
          break
        case 'copy':
          this.copyOrderNumber(order)
          break
      }
    },

    // 打印订单
    printOrder(order) {
      window.print()
      this.$message.success('打印请求已发送')
    },

    // 发送邮件
    sendEmail(order) {
      this.$prompt('请输入收件人邮箱：', '发送邮件', {
        confirmButtonText: '发送',
        cancelButtonText: '取消',
        inputPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        inputErrorMessage: '请输入正确的邮箱格式'
      }).then(({ value }) => {
        // 这里应该调用发送邮件的API
        this.$message.success(`订单信息已发送至 ${value}`)
      }).catch(() => {})
    },

    // 复制订单号
    copyOrderNumber(order) {
      const orderNo = order.orderNo || order.id || '未知订单号'
      if (navigator.clipboard) {
        navigator.clipboard.writeText(orderNo).then(() => {
          this.$message.success('订单号已复制到剪贴板')
        })
      } else {
        // 兼容旧浏览器
        const textArea = document.createElement('textarea')
        textArea.value = orderNo
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$message.success('订单号已复制到剪贴板')
      }
    },

    // 导出数据
    async handleExport() {
      this.exporting = true
      try {
        // 这里应该调用导出API
        await new Promise(resolve => setTimeout(resolve, 2000)) // 模拟API调用
        this.$message.success('订单数据导出成功')
      } catch (error) {
        this.$message.error('导出失败，请稍后重试')
      } finally {
        this.exporting = false
      }
    },

    // 批量导出
    batchExport() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择要导出的订单')
        return
      }
      this.handleExport()
    },

    // 批量取消
    batchCancel() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择要取消的订单')
        return
      }

      const payableOrders = this.selectedOrders.filter(order => order.CanCancel)
      if (payableOrders.length === 0) {
        this.$message.warning('选中的订单中没有可以取消的订单')
        return
      }

      this.$confirm(`确认取消选中的 ${payableOrders.length} 个订单？`, '批量取消', {
        confirmButtonText: '确认取消',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 这里应该调用批量取消的API
        this.$message.success(`已取消 ${payableOrders.length} 个订单`)
        this.selectedOrders = []
        this.loadOrderList()
      }).catch(() => {})
    },

    // 获取订单状态类型
    getStatusType(status) {
      const statusMap = {
        '待支付': 'warning',
        '已支付': 'success',
        '已出票': 'primary',
        '已取消': 'danger',
        '已完成': 'info'
      }
      return statusMap[status] || 'info'
    },

    // 获取订单状态文本
    getStatusText(status) {
      return status || '未知状态'
    },

    // 获取订单状态文本
    getOrderStatusText(status) {
      const statusMap = {
        'New': '新建',
        'UnPay': '待支付',
        'Paid': '已支付',
        'Ticketed': '已出票',
        'Completed': '已完成',
        'Cancelled': '已取消',
        'Refunded': '已退票',
        'Changed': '已改签',
        'Void': '已作废',
        'Pending': '处理中',
        '待支付': '待支付',
        '已支付': '已支付',
        '已出票': '已出票',
        '已取消': '已取消',
        '已完成': '已完成',
        '已退票': '已退票',
        '已改签': '已改签',
        '新建': '新建'
      }
      return statusMap[status] || status || '未知状态'
    },

    // 获取支付状态文本
    getPaymentStatusText(status) {
      const statusMap = {
        'UnPay': '未支付',
        'Pay': '支付中',
        'Paid': '已支付',
        'Refunded': '已退款',
        'PartPaid': '部分支付',
        'PartialPaid': '部分支付',
        'Pending': '支付中',
        '未支付': '未支付',
        '支付中': '支付中',
        '已支付': '已支付',
        '已退款': '已退款',
        '部分支付': '部分支付'
      }
      return statusMap[status] || status || '未知状态'
    },

    // 获取出票状态文本
    getTicketStatusText(status) {
      const statusMap = {
        'NotTicketed': '未出票',
        'Ticketing': '出票中',
        'Ticketed': '已出票',
        'ETicketed': '电子票',
        'PaperTicket': '纸质票',
        'Refunded': '已退票',
        'Void': '已作废',
        'Reissued': '已重开',
        'Pending': '处理中',
        '未出票': '未出票',
        '出票中': '出票中',
        '已出票': '已出票',
        '电子票': '电子票',
        '纸质票': '纸质票',
        '已退票': '已退票',
        '已作废': '已作废',
        '已重开': '已重开'
      }
      return statusMap[status] || status || '未知状态'
    },

    // 获取订单类型文本
    getOrderTypeText(type) {
      const typeMap = {
        'Flight': '机票',
        'Hotel': '酒店',
        'Train': '火车票',
        'Insurance': '保险',
        'Visa': '签证',
        'Tour': '旅游',
        'Car': '租车',
        'Combined': '组合产品',
        '机票': '机票',
        '酒店': '酒店',
        '火车票': '火车票',
        '保险': '保险',
        '签证': '签证',
        '旅游': '旅游',
        '租车': '租车',
        '组合产品': '组合产品'
      }
      return typeMap[type] || type || '未知类型'
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount || amount === 0) return '0.00'
      return parseFloat(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    // 格式化日期
    formatDate(date, format = 'yyyy-MM-dd hh:mm:ss') {
      if (!date) return '-'

      try {
        const d = new Date(date)
        if (isNaN(d.getTime())) return date

        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        const hours = String(d.getHours()).padStart(2, '0')
        const minutes = String(d.getMinutes()).padStart(2, '0')
        const seconds = String(d.getSeconds()).padStart(2, '0')

        return format
          .replace('yyyy', year)
          .replace('MM', month)
          .replace('dd', day)
          .replace('hh', hours)
          .replace('mm', minutes)
          .replace('ss', seconds)
      } catch (error) {
        return date
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.order-list {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;

  .page-header {
    background: white;
    margin-bottom: 20px;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;
    }

    .title-section {
      flex: 1;
    }

    .page-title {
      font-size: 28px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 8px 0;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: #409EFF;
        font-size: 24px;
      }
    }

    .page-subtitle {
      color: #909399;
      font-size: 14px;
      margin: 0;
      font-weight: normal;
    }

    .stats-section {
      display: flex;
      gap: 30px;
      align-items: center;

      .stat-item {
        text-align: center;
        min-width: 100px;

        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #409EFF;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: #909399;
        }
      }
    }
  }

  .search-form {
    background: white;
    margin-bottom: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .search-collapse {
      border: none;

      ::v-deep .el-collapse-item__header {
        background: #f8f9fa;
        padding: 0 20px;
        border-bottom: 1px solid #e4e7ed;
        font-weight: 500;
      }

      ::v-deep .el-collapse-item__content {
        padding: 20px;
      }
    }

    .search-form-content {
      margin: 0;

      .el-form-item {
        margin-bottom: 20px;
        margin-right: 0;

        .el-form-item__label {
          font-weight: 500;
          color: #606266;
        }
      }
    }
  }

  .quick-filters {
    background: white;
    padding: 15px 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;

    .el-button {
      border-radius: 20px;
    }
  }

  .order-table {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e4e7ed;
      background: #fafbfc;

      .table-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }

      .table-actions {
        .el-button-group {
          .el-button {
            border-radius: 4px;

            &:first-child {
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            }

            &:last-child {
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;
            }
          }
        }
      }
    }

    .price {
      color: #f56c6c;
      font-weight: bold;
      font-size: 14px;
    }

    .original-price {
      font-size: 12px;
      color: #909399;
      margin-top: 2px;
    }

    .order-cell {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .order-tags {
        display: flex;
        gap: 4px;
      }
    }

    .amount-cell {
      text-align: right;
    }

    .time-cell {
      .time {
        font-size: 12px;
        color: #909399;
      }
    }

    .flight-cell {
      .flight-intro {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 6px;
        font-weight: 500;
        color: #409EFF;

        i {
          color: #409EFF;
          font-size: 14px;
        }
      }

      .flight-route {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 6px;
        font-weight: 500;

        i {
          color: #909399;
        }
      }

      .flight-details {
        display: flex;
        align-items: center;
        gap: 8px;

        .flight-date {
          font-size: 12px;
          color: #909399;
        }
      }
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      justify-content: center;
    }

    .no-data {
      color: #c0c4cc;
      font-style: italic;
    }

    .pagination-wrapper {
      padding: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #e4e7ed;
      background: #fafbfc;

      .pagination-info {
        font-size: 13px;
        color: #606266;
      }
    }
  }

  .batch-actions {
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 15px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 20px;
    z-index: 1000;
    border: 1px solid #e4e7ed;

    .batch-info {
      font-size: 14px;
      color: #303133;
    }
  }

  // Element UI 样式增强
  ::v-deep .el-table {
    .el-table__header {
      background: #fafbfc;

      th {
        background: #fafbfc;
        color: #606266;
        font-weight: 600;
        border-bottom: 2px solid #e4e7ed;
      }
    }

    .el-table__row {
      &:hover {
        background: #f5f7fa;
      }
    }

    .el-link {
      font-weight: 500;
      font-size: 13px;

      &:hover {
        color: #66b1ff;
      }
    }

    .el-tag {
      font-weight: 500;
      font-size: 12px;
    }

    .el-button--mini.is-circle {
      padding: 4px;

      &:hover {
        transform: scale(1.1);
        transition: transform 0.2s;
      }
    }

    .el-loading-mask {
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(2px);
    }

    .el-table__empty-block {
      padding: 60px 0;

      .el-table__empty-text {
        color: #909399;
        font-size: 14px;
      }
    }

    .el-table-column--selection .cell {
      padding-left: 14px;
      padding-right: 14px;
    }
  }

  ::v-deep .el-form {
    .el-input {
      .el-input__inner {
        border-radius: 4px;
        transition: all 0.3s;

        &:focus {
          border-color: #409EFF;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
        }
      }
    }

    .el-select {
      .el-input__inner {
        border-radius: 4px;
      }
    }

    .el-date-editor {
      .el-input__inner {
        border-radius: 4px;
      }
    }
  }

  ::v-deep .el-button {
    border-radius: 4px;
    font-weight: 500;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &.el-button--primary {
      background: linear-gradient(135deg, #66b1ff, #409EFF);
      border: none;
    }

    &.el-button--success {
      background: linear-gradient(135deg, #95d575, #67c23a);
      border: none;
    }

    &.el-button--danger {
      background: linear-gradient(135deg, #f78989, #f56c6c);
      border: none;
    }
  }

  // 响应式设计
  @media (max-width: 1200px) {
    .page-header .header-content {
      flex-direction: column;
      align-items: flex-start;
    }

    .stats-section {
      align-self: stretch;
      justify-content: space-around;
    }
  }

  @media (max-width: 768px) {
    padding: 10px;

    .page-header {
      padding: 20px;
      margin-bottom: 15px;

      .page-title {
        font-size: 22px;
      }

      .stats-section {
        gap: 15px;

        .stat-item {
          min-width: 80px;

          .stat-number {
            font-size: 20px;
          }
        }
      }
    }

    .search-form {
      margin-bottom: 15px;

      .search-form-content {
        .el-row {
          .el-col {
            margin-bottom: 15px;
          }
        }
      }
    }

    .quick-filters {
      padding: 12px 15px;
      margin-bottom: 15px;
    }

    .order-table {
      .table-header {
        padding: 15px;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }

      .pagination-wrapper {
        flex-direction: column;
        gap: 15px;
        padding: 15px;
      }
    }

    .batch-actions {
      left: 20px;
      right: 20px;
      transform: none;
      bottom: 20px;
    }
  }

  @media (max-width: 480px) {
    .page-header {
      padding: 15px;

      .stats-section {
        flex-direction: column;
        gap: 10px;

        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          min-width: auto;

          .stat-label {
            margin-bottom: 0;
          }
        }
      }
    }

    .quick-filters {
      .el-button {
        font-size: 12px;
        padding: 6px 12px;
      }
    }
  }

  // 打印样式
  @media print {
    .search-form,
    .quick-filters,
    .table-header,
    .pagination-wrapper,
    .batch-actions,
    .action-buttons {
      display: none !important;
    }

    .order-list {
      padding: 0;
      background: white;
    }

    .page-header {
      box-shadow: none;
      border-bottom: 2px solid #000;
      margin-bottom: 20px;
    }

    .order-table {
      box-shadow: none;
      border: 1px solid #000;
    }

    ::v-deep .el-table {
      .el-table__header th {
        background: #f0f0f0 !important;
        border-bottom: 1px solid #000 !important;
        color: #000 !important;
      }

      .el-table__row {
        border-bottom: 1px solid #e0e0e0;
      }
    }
  }

  // 暗色主题支持（可选）
  @media (prefers-color-scheme: dark) {
    background: #1a1a1a;

    .page-header,
    .search-form,
    .quick-filters,
    .order-table {
      background: #2d2d2d;
      color: #e0e0e0;
      border-color: #404040;
    }

    .page-title {
      color: #e0e0e0;
    }

    .page-subtitle {
      color: #a0a0a0;
    }
  }

  // 动画效果
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .slide-up-enter-active, .slide-up-leave-active {
    transition: all 0.3s;
  }

  .slide-up-enter, .slide-up-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }
}
</style>